///
/// EarthBrowser
///
/// Created by Matt Giger on 9/21/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The layer vocabulary the globe-only embed understands, shared between
/// the snippet generator (embedSnippet.ts) and the embed entry
/// (src/embed/main.ts) so the two can't drift apart.
///

/** Vector overlays toggled directly on EarthActivity (tile-token authed) */
export const cEmbedVectorLayers = ["borders", "division_labels", "roads"] as const

/**
 * Dataset layers the embed can construct: a curated display set of marker
 * layers and texture overlays. Excluded on purpose: nexrad (heavy 3D
 * volumes), synoptic (WeatherGridService compute), placemark (user redux
 * state that doesn't exist in embeds).
 */
export const cEmbedDatasetLayers = [
	// markers / entities
	"earthquake",
	"volcano",
	"wildfire",
	"hurricane",
	"alert",
	"webcam",
	"buoy",
	"satellite",
	"location",
	"city_weather",
	// texture overlays
	"cloud",
	"aurora",
	"sst",
	"precipitation",
	"radar",
] as const

export type EmbedDatasetLayer = (typeof cEmbedDatasetLayers)[number]

/** Everything a snippet's data-layers / the embed's ?layers= may contain */
export const cEmbedLayers: readonly string[] = [...cEmbedVectorLayers, ...cEmbedDatasetLayers]

/**
 * Layers only the first-party video render mode (embed.html?render=1)
 * constructs, on top of the public embed set: the synoptic picture (the
 * master toggle plus its fronts / jet stream / upper-air sub-layers, all
 * computed in-page from the weather grids) and the NEXRAD radar volumes.
 * Too heavy or too compute-bound for third-party embeds, fine for a
 * render container. Names are the app's visibleDatasets ids, so a scene
 * exported from the app (utils/renderScene.ts) maps straight through.
 */
export const cRenderOnlyLayers = ["synoptic", "fronts", "jetstream", "upper_air", "nexrad", "radar_forecast", "cloud_forecast"] as const

export type RenderOnlyLayer = (typeof cRenderOnlyLayers)[number]

/** Sub-layers that imply the synoptic master toggle */
export const cSynopticSubLayers: readonly RenderOnlyLayer[] = ["fronts", "jetstream", "upper_air"]

/**
 * Entity target types (parseTarget's DatasetType values) the embed can
 * follow via ?target=: the EmbedDatasetLayer that must be enabled and the
 * DatasetLayerManager layer name used to focus the feature once its data
 * syncs. placemark and nexrad targets are absent on purpose - neither can
 * exist in an embed (user redux state / excluded heavy volumes), so their
 * snippets stay camera-only.
 */
export const cEmbedTargetTypes: Record<string, { layer: EmbedDatasetLayer; layerName: string }> = {
	hurricane: { layer: "hurricane", layerName: "hurricanes" },
	earthquake: { layer: "earthquake", layerName: "earthquakes" },
	volcano: { layer: "volcano", layerName: "volcanoes" },
	wildfire: { layer: "wildfire", layerName: "wildfires" },
	alert: { layer: "alert", layerName: "alerts" },
	webcam: { layer: "webcam", layerName: "webcams" },
	buoy: { layer: "buoy", layerName: "buoys" },
	satellite: { layer: "satellite", layerName: "satellites" },
	location: { layer: "location", layerName: "locations" },
}

/**
 * Curated embed layer sets per focused-feature type, keyed by the info
 * window's contentData.type. Used by the info panels' "Copy embed code"
 * button: the feature's own layer plus the context layers that read well
 * in a small display-only globe.
 *
 * Guiding choices: storms get cloud + precipitation texture context;
 * severe-weather features get the radar mosaic and city weather; ocean
 * features get SST; geology gets clean political context only (clouds
 * would obscure markers/smoke); ground-level features (webcams,
 * placemarks) get roads.
 */
export const cEmbedFeatureLayers: Record<string, readonly string[]> = {
	hurricane: ["hurricane", "cloud", "precipitation", "borders"],
	alert: ["alert", "radar", "city_weather", "borders"],
	// The nexrad station volumes are excluded from embeds; the global radar
	// mosaic shows the same weather at embed scale
	radar: ["radar", "city_weather", "borders"],
	location: ["city_weather", "cloud", "radar", "borders"],
	earthquake: ["earthquake", "borders", "division_labels"],
	volcano: ["volcano", "borders", "division_labels"],
	wildfire: ["wildfire", "borders", "division_labels"],
	buoy: ["buoy", "sst", "borders"],
	webcam: ["webcam", "borders", "roads"],
	satellite: ["satellite", "borders"],
	// Placemarks are user redux state that can't render in embeds; the
	// snippet still frames the place with ground-level context
	placemark: ["borders", "roads", "city_weather"],
}
